// ============================================================================
// Pins — a session's working set of documents & snippets
// ============================================================================
// Two tiers:
//   session pins — attached to the session fv/chat was opened from
//   global pins  — appear in every session's strip (long-press / right-click)
// Pins made with no session context are global; pins made while a session is
// still starting (no sessionId yet) go to a "pending" bucket that migrates to
// the real id when onSessionChanged() sees it.
//
// Depends on: haptic (shared.js global), Token (shared.js global),
// marked/hljs (CDN globals for peek rendering).

import {
  pinStrip, pinPeekOverlay, pinPeekTitle, pinPeekBody,
  pinPeekOpen, pinPeekUnpin, pinPeekClose,
} from "./dom.js";
import { loadPinsMap, savePinsMap } from "./persistence.js";
import { copyText, flashCopied } from "./copy.js";
import { icon as svgIcon } from "./icons.js";

// ---- deps set by initPins ----
let getSessionKeyRef = () => null;        // () => sessionId | "pending" | null
let openFileAtRef = null;                 // (path, name) => void — open in fv
let toastRef = (msg) => console.log(msg);

const PIN_GLYPH = () => svgIcon("pin", 13);
const PIN_FILL_GLYPH = () => svgIcon("pinFill", 13);
const GLOBE_GLYPH = () => svgIcon("globe", 12);

function toast(msg) { toastRef(msg); }

function pinKey(pin) {
  return pin.kind === "file" ? "file:" + pin.path : "snip:" + pin.id;
}

function pinLabel(pin) {
  return pin.kind === "file" ? pin.name : pin.title;
}

/** Find a pin's tier for the current session: "session" | "global" | null. */
function findPin(pin, sessionKey) {
  const map = loadPinsMap();
  const k = pinKey(pin);
  if (map.global.some((p) => pinKey(p) === k)) return "global";
  if (sessionKey && sessionKey !== "global") {
    const arr = sessionKey === "pending" ? map.pending : (map.sessions[sessionKey] || []);
    if (arr.some((p) => pinKey(p) === k)) return "session";
  }
  return null;
}

/**
 * Add a pin. `sessionKey` is the fv/chat session the pin came from:
 * a real sessionId, "pending" (session starting), or null → global tier.
 * `global: true` forces the global tier regardless of sessionKey.
 */
export function addPin(pin, { sessionKey = null, global = false } = {}) {
  const map = loadPinsMap();
  if (pin.kind === "snippet" && !pin.id) pin.id = "s" + Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
  const k = pinKey(pin);
  let arr;
  if (global || !sessionKey) arr = map.global;
  else if (sessionKey === "pending") arr = map.pending;
  else arr = map.sessions[sessionKey] = map.sessions[sessionKey] || [];
  if (arr.some((p) => pinKey(p) === k)) { toast("Already pinned"); return; }
  arr.push(pin);
  savePinsMap(map);
  renderPinStrip();
  toast((global || !sessionKey ? "Pinned globally" : "Pinned") + " — " + pinLabel(pin));
}

function removePin(pin, tier, sessionKey) {
  const map = loadPinsMap();
  const k = pinKey(pin);
  const arr = tier === "global" ? map.global
    : sessionKey === "pending" ? map.pending
    : (map.sessions[sessionKey] || []);
  const i = arr.findIndex((p) => pinKey(p) === k);
  if (i < 0) return;
  arr.splice(i, 1);
  savePinsMap(map);
  renderPinStrip();
}

/** Pin/unpin a file for a session (null session → global tier). */
export function toggleFilePin(path, name, sessionKey) {
  const pin = { kind: "file", path, name };
  const tier = findPin(pin, sessionKey);
  if (tier === "session" || (!sessionKey && tier === "global")) {
    removePin(pin, tier, sessionKey);
    toast("Unpinned — " + name);
  } else {
    addPin(pin, { sessionKey });
  }
}

/** Toggle a file's global pin (survives every session switch). */
export function toggleFilePinGlobal(path, name) {
  const pin = { kind: "file", path, name };
  const map = loadPinsMap();
  const k = pinKey(pin);
  if (map.global.some((p) => pinKey(p) === k)) {
    removePin(pin, "global", null);
    toast("Unpinned globally — " + name);
  } else {
    addPin(pin, { global: true });
  }
}

/** Report which tiers a file is pinned in, for context-menu labels. */
export function filePinTier(path, sessionKey) {
  return findPin({ kind: "file", path }, sessionKey);
}

/**
 * Called whenever the active sessionId changes. Migrates the "pending"
 * bucket into the first real session id it sees, then re-renders the strip.
 */
export function onSessionChanged(id) {
  const map = loadPinsMap();
  if (id && map.pending.length) {
    const arr = map.sessions[id] = map.sessions[id] || [];
    for (const p of map.pending) {
      if (!arr.some((q) => pinKey(q) === pinKey(p))) arr.push(p);
    }
    map.pending = [];
    savePinsMap(map);
  }
  renderPinStrip();
}

/** Drop a deleted session's pins (globals are untouched). */
export function deleteSessionPins(id) {
  const map = loadPinsMap();
  if (map.sessions[id]) {
    delete map.sessions[id];
    savePinsMap(map);
  }
}

// ---- pin strip (under the chat header) ----

/** Rebuild the strip: global ∪ current-session pins, hidden when empty. */
export function renderPinStrip() {
  if (!pinStrip) return;
  const sessionKey = getSessionKeyRef();
  const map = loadPinsMap();
  const sessionArr = sessionKey === "pending" ? map.pending
    : (sessionKey ? (map.sessions[sessionKey] || []) : []);
  const pins = [
    ...map.global.map((p) => ({ pin: p, global: true })),
    ...sessionArr
      .filter((p) => !map.global.some((g) => pinKey(g) === pinKey(p)))
      .map((p) => ({ pin: p, global: false })),
  ];
  pinStrip.innerHTML = "";
  if (!pins.length) { pinStrip.hidden = true; return; }
  pinStrip.hidden = false;
  for (const { pin, global } of pins) {
    const chip = document.createElement("button");
    chip.className = "pin-chip" + (global ? " pin-chip-global" : "");
    chip.type = "button";
    chip.title = (global ? "Global pin — " : "Pinned — ") +
      (pin.kind === "file" ? pin.path : pin.title) +
      (pin.kind === "file" ? " · click to open in Files" : " · click to view") +
      " · right-click / long-press for options";
    const glyph = document.createElement("span");
    glyph.className = "pin-chip-glyph";
    // Tier glyph: filled pin = session pin, globe = global pin. Kind glyph
    // follows for files so the chip still reads as a document.
    glyph.innerHTML = (global ? GLOBE_GLYPH() : PIN_FILL_GLYPH()) +
      (pin.kind === "file" ? svgIcon("file", 12) : '<span class="pin-snip-glyph">&lt;/&gt;</span>');
    chip.appendChild(glyph);
    const lbl = document.createElement("span");
    lbl.className = "pin-chip-label";
    lbl.textContent = pinLabel(pin);
    chip.appendChild(lbl);
    chip.addEventListener("click", () => { haptic("tap"); openPeek(pin); });
    // Right-click / long-press → tier + unpin options.
    const menu = (x, y) => showPinMenu(x, y, pin, global, sessionKey);
    chip.addEventListener("contextmenu", (e) => { e.preventDefault(); haptic("tap"); menu(e.clientX, e.clientY); });
    attachLongPress(chip, menu);
    pinStrip.appendChild(chip);
  }
}

function attachLongPress(el, fn) {
  let timer = null, sx = 0, sy = 0;
  el.addEventListener("touchstart", (e) => {
    if (e.touches.length !== 1) return;
    const t = e.touches[0];
    sx = t.clientX; sy = t.clientY;
    timer = setTimeout(() => { haptic("tap"); fn(t.clientX, t.clientY); }, 500);
  }, { passive: true });
  const cancel = () => { if (timer) { clearTimeout(timer); timer = null; } };
  el.addEventListener("touchmove", (e) => {
    if (!timer) return;
    const t = e.touches[0];
    if (Math.abs(t.clientX - sx) > 10 || Math.abs(t.clientY - sy) > 10) cancel();
  }, { passive: true });
  el.addEventListener("touchend", cancel);
  el.addEventListener("touchcancel", cancel);
}

function showPinMenu(x, y, pin, isGlobal, sessionKey) {
  document.querySelectorAll(".pin-menu").forEach((m) => m.remove());
  const menu = document.createElement("div");
  menu.className = "pin-menu fv-menu";
  const items = [
    isGlobal
      ? { label: "Make session-only", onClick: () => { moveTier(pin, "session", sessionKey); } }
      : { label: "Pin globally", onClick: () => { moveTier(pin, "global", sessionKey); } },
    { label: "Unpin", destructive: true, onClick: () => removePin(pin, isGlobal ? "global" : "session", sessionKey) },
  ];
  for (const it of items) {
    const btn = document.createElement("button");
    btn.className = "fv-menu-item" + (it.destructive ? " fv-menu-destructive" : "");
    btn.innerHTML = '<span class="fv-menu-label"></span>';
    btn.querySelector(".fv-menu-label").textContent = it.label;
    btn.addEventListener("click", (e) => { e.stopPropagation(); haptic("tap"); menu.remove(); it.onClick(); });
    menu.appendChild(btn);
  }
  document.body.appendChild(menu);
  const rect = menu.getBoundingClientRect();
  menu.style.left = Math.max(8, Math.min(x, window.innerWidth - rect.width - 8)) + "px";
  menu.style.top = Math.max(8, Math.min(y, window.innerHeight - rect.height - 8)) + "px";
  const dismiss = () => { menu.remove(); document.removeEventListener("keydown", keyHandler); };
  const keyHandler = (e) => { if (e.key === "Escape") { e.preventDefault(); dismiss(); } };
  requestAnimationFrame(() => {
    document.addEventListener("click", dismiss, { once: true });
    document.addEventListener("contextmenu", dismiss, { once: true });
  });
  document.addEventListener("keydown", keyHandler);
}

/** Move a pin between the global and session tiers. */
function moveTier(pin, to, sessionKey) {
  const map = loadPinsMap();
  const k = pinKey(pin);
  const from = to === "global"
    ? (sessionKey === "pending" ? map.pending : (map.sessions[sessionKey] || []))
    : map.global;
  const i = from.findIndex((p) => pinKey(p) === k);
  if (i >= 0) from.splice(i, 1);
  const dest = to === "global" ? map.global
    : sessionKey === "pending" ? map.pending
    : (map.sessions[sessionKey] = map.sessions[sessionKey] || []);
  if (!dest.some((p) => pinKey(p) === k)) dest.push(pin);
  savePinsMap(map);
  renderPinStrip();
  toast(to === "global" ? "Pinned globally" : "Pinned to session");
}

// ---- peek popover ----

export function isPinPeekOpen() { return !pinPeekOverlay.hidden; }

export function closePinPeek() { pinPeekOverlay.hidden = true; }

/**
 * Click a pin: file pins go straight to the file viewer (no intermediary —
 * the viewer already has unpin). Snippet pins get a read-only peek with
 * copy, since snippets don't live in the file viewer.
 */
export async function openPeek(pin) {
  if (pin.kind === "file") {
    if (openFileAtRef) openFileAtRef(pin.path, pin.name);
    return;
  }
  pinPeekTitle.textContent = pinLabel(pin);
  pinPeekTitle.title = pin.title;
  pinPeekBody.innerHTML = "";
  pinPeekOpen.style.display = "none";
  pinPeekUnpin.style.display = "";
  pinPeekUnpin.onclick = () => {
    haptic("tap");
    const tier = findPin(pin, getSessionKeyRef());
    if (tier) removePin(pin, tier, getSessionKeyRef());
    closePinPeek();
  };
  pinPeekOverlay.hidden = false;
  renderSnippetPeek(pin);
}

function renderSnippetPeek(pin) {
  const head = document.createElement("div");
  head.className = "pin-peek-actions";
  const copyBtn = document.createElement("button");
  copyBtn.className = "ghost small";
  copyBtn.textContent = "Copy";
  copyBtn.addEventListener("click", () => {
    haptic("tap");
    void copyText(pin.text).then((ok) => { if (ok) flashCopied(copyBtn); });
  });
  head.appendChild(copyBtn);
  const pre = document.createElement("pre");
  const code = document.createElement("code");
  code.className = "hljs" + (pin.lang ? " language-" + pin.lang : "");
  code.textContent = pin.text;
  try {
    if (pin.lang && typeof hljs !== "undefined" && hljs.getLanguage(pin.lang)) {
      code.innerHTML = hljs.highlight(pin.text, { language: pin.lang }).value;
    } else if (typeof hljs !== "undefined") {
      code.innerHTML = hljs.highlightAuto(pin.text).value;
    }
  } catch { /* keep plain text */ }
  pre.appendChild(code);
  pinPeekBody.appendChild(head);
  pinPeekBody.appendChild(pre);
}

// ---- snippet pins on chat code blocks ----

/**
 * Post-render pass: add a 📌 button next to every wrapped <pre>'s copy
 * button. Call after addCopyButtons(root). Idempotent via data-pin-wired.
 */
export function addPinButtons(root) {
  if (!root || !root.querySelectorAll) return;
  for (const pre of root.querySelectorAll("pre:not([data-pin-wired])")) {
    pre.dataset.pinWired = "1";
    const wrap = pre.closest(".copy-wrap");
    if (!wrap) continue;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "pin-btn";
    btn.title = "Pin snippet (right-click / long-press: pin globally)";
    btn.innerHTML = PIN_GLYPH();
    btn.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      pinFromPre(pre, false, btn);
    });
    btn.addEventListener("contextmenu", (e) => {
      e.preventDefault();
      e.stopPropagation();
      pinFromPre(pre, true, btn);
    });
    attachLongPress(btn, () => pinFromPre(pre, true, btn));
    wrap.appendChild(btn);
  }
}

function pinFromPre(pre, global, btn) {
  const code = pre.querySelector("code");
  const text = (code || pre).innerText;
  if (!text.trim()) { toast("Nothing to pin"); return; }
  let lang = "";
  const cls = code ? [...code.classList].find((c) => c.startsWith("language-")) : null;
  if (cls) lang = cls.slice(9);
  const title = text.trim().split("\n")[0].slice(0, 48) || "snippet";
  addPin(
    { kind: "snippet", title: "snippet: " + title, lang, text },
    { sessionKey: getSessionKeyRef(), global },
  );
  if (btn) { btn.innerHTML = PIN_FILL_GLYPH(); btn.classList.add("pinned"); }
}

// ---- init ----

export function initPins(deps) {
  getSessionKeyRef = deps.getSessionKey || (() => null);
  openFileAtRef = deps.openFileAt || null;
  toastRef = deps.toast || toastRef;

  pinPeekClose.addEventListener("click", () => { haptic("tap"); closePinPeek(); });
  pinPeekOverlay.addEventListener("click", (e) => {
    if (e.target === pinPeekOverlay) { haptic("tap"); closePinPeek(); }
  });
  renderPinStrip();
}
